<template>
  <div class="rfp-control">
    <button class="rfp-trigger" type="button" @click="openModal">
      <svg class="rfp-trigger-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 6h16"/>
        <path d="M4 12h10"/>
        <path d="M4 18h7"/>
        <path d="M18 14v6"/>
        <path d="M15 17h6"/>
      </svg>
      Fields
      <span class="rfp-count">{{ visibleCount }}/{{ totalCount }}</span>
    </button>

    <modal
      :name="modalName"
      class="rfp-modal"
      :adaptive="true"
      :scrollable="true"
      height="auto"
      width="90%"
      :maxWidth="620"
      @before-open="prepareDraft"
    >
      <div class="rfp-dialog">
        <div class="rfp-dialog-header">
          <div class="rfp-dialog-header-left">
            <span class="rfp-dialog-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 6h16"/>
                <path d="M4 12h16"/>
                <path d="M4 18h16"/>
              </svg>
            </span>
            <div>
              <h2 class="rfp-dialog-title">{{ title }}</h2>
              <p class="rfp-dialog-sub">{{ subtitle }}</p>
            </div>
          </div>
          <button class="rfp-close-btn" type="button" @click="closeModal" title="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="rfp-dialog-body">
          <div class="rfp-summary">
            <span class="rfp-summary-label">{{ draftVisibleCount }} selected</span>
            <span class="rfp-summary-text">Locked fields stay visible for navigation and actions.</span>
          </div>

          <div v-for="group in groupedFields" :key="group.name" class="rfp-group">
            <h3 class="rfp-group-title">{{ group.name }}</h3>
            <div class="rfp-field-grid">
              <label v-for="field in group.fields" :key="field.id" class="rfp-option" :class="{ 'rfp-option--locked': field.locked }">
                <input
                  class="rfp-checkbox"
                  type="checkbox"
                  :checked="isDraftSelected(field.id)"
                  :disabled="field.locked"
                  @change="toggleDraft(field.id)"
                />
                <span class="rfp-checkmark">
                  <svg v-if="isDraftSelected(field.id)" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </span>
                <span class="rfp-option-text">
                  <span class="rfp-option-label">{{ field.label }}</span>
                  <span v-if="field.locked" class="rfp-locked">Locked</span>
                </span>
              </label>
            </div>
          </div>
        </div>

        <div class="rfp-dialog-footer">
          <button class="rfp-reset-btn" type="button" @click="resetDefaults">Reset defaults</button>
          <button class="rfp-cancel-btn" type="button" @click="closeModal">Cancel</button>
          <button class="rfp-save-btn" type="button" @click="applySelection">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
            Apply
          </button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getDefaultRunnerFieldIds, sanitizeRunnerFieldIds } from '@/utils/runnerFieldPreferences'

export default {
  name: 'runner-field-visibility-control',
  props: {
    modalName: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: 'Visible fields'
    },
    subtitle: {
      type: String,
      default: 'Choose the fields shown on this page'
    },
    fieldOptions: {
      type: Array,
      default: function () { return [] }
    },
    selectedFields: {
      type: Array,
      default: function () { return [] }
    }
  },
  data: function () {
    return {
      draftSelectedFields: []
    }
  },
  computed: {
    totalCount: function () {
      return this.fieldOptions.length
    },
    visibleCount: function () {
      return sanitizeRunnerFieldIds(this.selectedFields, this.fieldOptions).length
    },
    draftVisibleCount: function () {
      return sanitizeRunnerFieldIds(this.draftSelectedFields, this.fieldOptions).length
    },
    groupedFields: function () {
      var groups = []
      this.fieldOptions.forEach(function (field) {
        var groupName = field.group || 'Fields'
        var group = groups.find(function (item) { return item.name === groupName })
        if (!group) {
          group = { name: groupName, fields: [] }
          groups.push(group)
        }
        group.fields.push(field)
      })
      return groups
    }
  },
  methods: {
    openModal: function () {
      this.prepareDraft()
      this.$modal.show(this.modalName)
    },
    closeModal: function () {
      this.$modal.hide(this.modalName)
    },
    prepareDraft: function () {
      this.draftSelectedFields = sanitizeRunnerFieldIds(this.selectedFields, this.fieldOptions).slice()
    },
    isDraftSelected: function (id) {
      return this.draftSelectedFields.indexOf(id) !== -1
    },
    isLocked: function (id) {
      return this.fieldOptions.some(function (field) {
        return field.id === id && field.locked
      })
    },
    toggleDraft: function (id) {
      if (this.isLocked(id)) return
      var nextFields = this.draftSelectedFields.slice()
      var index = nextFields.indexOf(id)
      if (index === -1) {
        nextFields.push(id)
      } else {
        nextFields.splice(index, 1)
      }
      this.draftSelectedFields = sanitizeRunnerFieldIds(nextFields, this.fieldOptions)
    },
    resetDefaults: function () {
      this.draftSelectedFields = getDefaultRunnerFieldIds(this.fieldOptions)
      this.$emit('reset')
      this.closeModal()
    },
    applySelection: function () {
      this.$emit('apply', sanitizeRunnerFieldIds(this.draftSelectedFields, this.fieldOptions))
      this.closeModal()
    }
  }
}
</script>

<style>
.rfp-control {
  display: inline-flex;
}

.rfp-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-width: 0;
  height: 33px;
  border: 1.5px solid #5b4fcf;
  border-radius: 7px;
  background: #fff;
  color: #5b4fcf;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 0 12px;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.rfp-trigger:hover {
  background: #5b4fcf;
  border-color: #5b4fcf;
  color: #fff;
}

.rfp-trigger-icon {
  width: 13px;
  height: 13px;
}

.rfp-count {
  display: inline-flex;
  align-items: center;
  min-height: 18px;
  border-radius: 999px;
  background: #f0effe;
  color: #413b94;
  font-size: 10px;
  font-weight: 700;
  padding: 0 7px;
}

.rfp-trigger:hover .rfp-count {
  background: rgba(255,255,255,0.2);
  color: #fff;
}

.rfp-dialog {
  overflow: hidden;
  border-radius: 14px;
  background: #fff;
  color: #1a1a2e;
  font-family: 'Inter', sans-serif;
}

.rfp-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 1px solid #f0f0f5;
  padding: 1rem 1.75rem;
}

.rfp-dialog-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.rfp-dialog-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #f0effe;
  color: #5b4fcf;
}

.rfp-dialog-icon svg {
  width: 16px;
  height: 16px;
}

.rfp-dialog-title {
  margin: 0;
  color: #1a1a2e;
  font-size: 16px;
  font-weight: 700;
}

.rfp-dialog-sub {
  margin: 3px 0 0;
  color: #8a8a9a;
  font-size: 12px;
}

.rfp-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border: 1px solid #e8e8ed;
  border-radius: 50%;
  background: #fff;
  color: #8a8a9a;
  cursor: pointer;
}

.rfp-close-btn svg {
  width: 14px;
  height: 14px;
}

.rfp-close-btn:hover {
  border-color: #c0392b;
  color: #c0392b;
}

.rfp-dialog-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 62vh;
  overflow: auto;
  padding: 1.5rem 1.75rem;
}

.rfp-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid #e8e8ed;
  border-radius: 10px;
  background: #fafafa;
  padding: 0.75rem 0.9rem;
}

.rfp-summary-label {
  color: #413b94;
  font-size: 12px;
  font-weight: 800;
}

.rfp-summary-text {
  color: #8a8a9a;
  font-size: 12px;
}

.rfp-group {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.rfp-group-title {
  margin: 0;
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 700;
}

.rfp-field-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
}

.rfp-option {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  border: 1px solid #e8e8ed;
  border-radius: 9px;
  background: #fff;
  cursor: pointer;
  padding: 0.7rem 0.75rem;
  transition: border-color 0.15s, background 0.15s;
}

.rfp-option:hover {
  border-color: #5b4fcf;
  background: #fafafa;
}

.rfp-option--locked {
  cursor: default;
}

.rfp-checkbox {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.rfp-checkmark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 19px;
  height: 19px;
  border: 1.5px solid #d0d0e0;
  border-radius: 6px;
  color: #5b4fcf;
}

.rfp-checkmark svg {
  width: 13px;
  height: 13px;
}

.rfp-option-text {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  width: 100%;
}

.rfp-option-label {
  overflow: hidden;
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rfp-locked {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  min-height: 18px;
  border-radius: 999px;
  background: #f0effe;
  color: #413b94;
  font-size: 10px;
  font-weight: 800;
  padding: 0 7px;
}

.rfp-dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.7rem;
  border-top: 1px solid #f0f0f5;
  padding: 1rem 1.75rem 1.5rem;
}

.rfp-reset-btn,
.rfp-cancel-btn,
.rfp-save-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  height: 36px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 0 18px;
}

.rfp-reset-btn,
.rfp-cancel-btn {
  border: 1.5px solid #e0e0ea;
  background: #fff;
  color: #5b4fcf;
}

.rfp-save-btn {
  border: none;
  background: #5b4fcf;
  color: #fff;
}

.rfp-save-btn svg {
  width: 13px;
  height: 13px;
}

.rfp-save-btn:hover {
  background: #4338ca;
}

@media (max-width: 640px) {
  .rfp-field-grid {
    grid-template-columns: 1fr;
  }

  .rfp-summary,
  .rfp-dialog-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .rfp-reset-btn,
  .rfp-cancel-btn,
  .rfp-save-btn {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// RunnerFieldVisibilityControl.vue?58e855fa